import { type ReactElement } from 'react';
import ContentMarkup from '@/components/content-markup';
import { cn } from '@/lib/utils';

/**
 * Styles applied to the footer links content-asset markup — sibling column `<div>`s each
 * containing an un-clickable `<h6>` title followed by `<li><a>` links — see the
 * `footer-links` content asset in Business Manager.
 * Matches the multibrand Figma: 14px medium black title, 12px `#474747` links.
 */
const FOOTER_LINKS_CONTENT_STYLES = [
    'grid grid-cols-1 gap-6 border-b border-footer-border pb-4 sm:grid-cols-3',
    'text-xs font-medium text-footer-muted-foreground',
    '[&>div]:flex [&>div]:flex-col [&>div]:gap-2',
    '[&_h6]:m-0 [&_h6]:mb-1 [&_h6]:text-sm [&_h6]:font-medium [&_h6]:uppercase [&_h6]:text-footer-foreground',
    '[&_li]:list-none [&_li]:leading-4',
    '[&_a]:transition-colors [&_a]:hover:text-footer-foreground',
].join(' ');

export interface FooterLinksSectionProps {
    /** Merchant-managed link columns markup loaded from the `footer-links` content asset. */
    links?: string;
    className?: string;
}

/**
 * Renders the footer's merchant-managed link columns. The markup comes straight from a
 * Business Manager content asset, so merchants can add, remove, or re-order columns and
 * links without a code change. Renders nothing when the content asset is not configured.
 */
export default function FooterLinksSection({ links, className }: FooterLinksSectionProps): ReactElement | null {
    if (!links) {
        return null;
    }

    return (
        <nav aria-label="Footer links">
            <ContentMarkup content={links} className={cn(FOOTER_LINKS_CONTENT_STYLES, className)} />
        </nav>
    );
}
